import * as React from 'react'

import { RotateCcw } from 'lucide-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { compareLabelsAlphanumeric } from '~/client/src/shared/utils/compareGroupsByLabel'

import { renderOptionGlyph } from '../optionGlyphs'
import OptionsBody from '../pieces/OptionsBody'
import OptionsEmpty from '../pieces/OptionsEmpty'
import OptionsHeader from '../pieces/OptionsHeader'
import OptionsItem from '../pieces/OptionsItem'
import OptionsItemList from '../pieces/OptionsItemList'
import OptionsPanel from '../pieces/OptionsPanel'
import OptionsSearch from '../pieces/OptionsSearch'
import OptionsToolbar from '../pieces/OptionsToolbar'
import OrderToggle from '../pieces/OrderToggle'
import { ISortByPanelProps } from '../types'

const SortByPanel: React.FC<ISortByPanelProps> = ({
  title,
  options,
  selectedKey,
  onChange,
  onClose,
  onBack,
  sortOrder,
  onSortOrderChange,
  allowNone = false,
  searchable = true,
  searchPlaceholder,
  onReset,
  autoFocus = true,
}) => {
  const [query, setQuery] = React.useState('')

  const sortedOptions = React.useMemo(
    () =>
      [...options].sort((a, b) => compareLabelsAlphanumeric(a.label, b.label)),
    [options],
  )

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sortedOptions
    return sortedOptions.filter(o => o.label.toLowerCase().includes(q))
  }, [sortedOptions, query])

  const hasIcons = options.some(o => !!o.glyph)
  const showNoneMatchesQuery =
    !query.trim() ||
    Localization.translator.none
      .toLowerCase()
      .includes(query.trim().toLowerCase())
  const showNone = allowNone && showNoneMatchesQuery

  const handlePick = (key: string | null) => {
    onChange(key)
    if (onBack) {
      onBack()
      return
    }
    onClose?.()
  }

  return (
    <OptionsPanel className="tw:h-full">
      <OptionsHeader
        title={title ?? Localization.translator.sort}
        onBack={onBack}
        onClose={onClose}
      />
      <OptionsBody>
        <OptionsToolbar>
          {searchable && (
            <OptionsSearch
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder}
              autoFocus={autoFocus}
            />
          )}
          {sortOrder !== undefined && onSortOrderChange && (
            <OrderToggle
              value={sortOrder}
              onChange={onSortOrderChange}
              disabled={selectedKey === null}
            />
          )}
          {onReset && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={Localization.translator.reset}
              onClick={onReset}
            >
              <RotateCcw />
            </Button>
          )}
        </OptionsToolbar>
        <OptionsItemList>
          {showNone && (
            <OptionsItem
              icon={hasIcons ? renderOptionGlyph('none') : undefined}
              reserveIconSlot={hasIcons}
              label={Localization.translator.none}
              selected={selectedKey === null}
              onClick={() => handlePick(null)}
            />
          )}
          {filtered.map(opt => (
            <OptionsItem
              key={opt.key}
              icon={renderOptionGlyph(opt.glyph)}
              reserveIconSlot={hasIcons}
              label={opt.label}
              selected={selectedKey === opt.key}
              onClick={() => handlePick(opt.key)}
            />
          ))}
          {filtered.length === 0 && !showNone && <OptionsEmpty />}
        </OptionsItemList>
      </OptionsBody>
    </OptionsPanel>
  )
}

export default SortByPanel
